import type { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model';
import { Selection, type Transaction } from '@tiptap/pm/state';
import { canJoin } from '@tiptap/pm/transform';
import { Extension } from '@tiptap/react';

const LIST_TYPES = ['bulletList', 'orderedList'];

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    listBoundaryKeymap: {
      /**
       * Backspace at the start of an empty paragraph that sits directly after a
       * list: drop the paragraph (and merge the now-adjacent same-type lists)
       * instead of letting StarterKit's list-keymap fold it into the last item.
       */
      deleteEmptyParagraphAfterList: () => ReturnType;
      /**
       * Delete at the end of a list's last item when the next block is an empty
       * paragraph: drop that paragraph and keep the caret where it is.
       */
      deleteEmptyParagraphBeforeCaretList: () => ReturnType;
    };
  }
}

function isListNode(node: ProseMirrorNode | null | undefined): boolean {
  return node != null && LIST_TYPES.includes(node.type.name);
}

function isEmptyParagraph(node: ProseMirrorNode | null | undefined): boolean {
  return node != null && node.type.name === 'paragraph' && node.content.size === 0;
}

/**
 * True only when the caret sits at the start of a top-level empty paragraph —
 * i.e. depth 1, parent offset 0, parent is an empty paragraph. Guards against
 * deeply nested empty paragraphs (inside a list item) where the adjacency check
 * would mis-resolve.
 */
function isCaretAtStartOfTopLevelEmptyParagraph($from: ResolvedPos): boolean {
  return $from.depth === 1 && $from.parentOffset === 0 && isEmptyParagraph($from.parent);
}

/**
 * True when the caret sits at the very end of the last item of the top-level
 * list at `listDepth` — at the end of its textblock, and the last child at
 * every level between that textblock and the list. Depth-agnostic so a nested
 * sublist's last item still resolves correctly.
 */
function isCaretAtEndOfLastListItem($from: ResolvedPos, listDepth: number): boolean {
  if ($from.parentOffset !== $from.parent.content.size) {
    return false;
  }
  for (let depth = $from.depth; depth > listDepth; depth -= 1) {
    if ($from.index(depth - 1) !== $from.node(depth - 1).childCount - 1) {
      return false;
    }
  }
  return true;
}

/**
 * After the empty paragraph between two lists is removed, the previous list and
 * the following node become neighbours at `boundaryPos`. Join them into one list
 * only when they are the SAME list type. `canJoin` alone is not enough: it
 * treats a bulleted and a numbered list as joinable (both hold `listItem`s), so
 * we additionally require an exact node-type match to keep different list types
 * separate.
 */
function joinAdjacentSameTypeLists(tr: Transaction, boundaryPos: number): void {
  const $boundary = tr.doc.resolve(boundaryPos);
  const before = $boundary.nodeBefore;
  const after = $boundary.nodeAfter;
  const sameListType = isListNode(before) && before!.type === after?.type;

  if (sameListType && canJoin(tr.doc, boundaryPos)) {
    tr.join(boundaryPos);
  }
}

function handleBackspace(tr: Transaction): boolean {
  const { $from } = tr.selection;

  if (!tr.selection.empty || !isCaretAtStartOfTopLevelEmptyParagraph($from)) {
    return false;
  }

  const paragraphIndex = $from.index(0);
  const previousNode = paragraphIndex > 0 ? tr.doc.child(paragraphIndex - 1) : null;
  if (!isListNode(previousNode)) {
    return false;
  }

  const paragraphStart = $from.before(1);
  const paragraphEnd = $from.after(1);

  tr.delete(paragraphStart, paragraphEnd);

  // `paragraphStart` is now the boundary between the previous list and whatever
  // block followed the paragraph.
  joinAdjacentSameTypeLists(tr, paragraphStart);

  // Caret to the end of the previous list's last item: snap to the nearest
  // selectable text position searching backward from the boundary.
  tr.setSelection(Selection.near(tr.doc.resolve(paragraphStart), -1));
  return true;
}

function handleDelete(tr: Transaction): boolean {
  const { $from } = tr.selection;

  if (!tr.selection.empty || $from.depth < 2) {
    return false;
  }

  // Walk up to the top-level list ancestor that holds the current item.
  const listDepth = 1;
  const list = $from.node(listDepth);
  if (!isListNode(list) || $from.node(0).type.name !== 'doc') {
    return false;
  }

  const listIndex = $from.index(0);
  const nextNode =
    listIndex + 1 < $from.node(0).childCount ? $from.node(0).child(listIndex + 1) : null;
  if (!isEmptyParagraph(nextNode)) {
    return false;
  }

  if (!isCaretAtEndOfLastListItem($from, listDepth)) {
    return false;
  }

  const listStart = $from.before(listDepth);
  const paragraphStart = listStart + list.nodeSize;
  const paragraphEnd = paragraphStart + nextNode!.nodeSize;
  tr.delete(paragraphStart, paragraphEnd);
  return true;
}

/**
 * Backspace/Delete keymap that deletes an empty paragraph sitting directly next
 * to a list, rather than merging it into the adjacent list item.
 *
 * StarterKit (^3.22.x) bundles `@tiptap/extension-list-keymap`, whose
 * `handleBackspace` runs `joinBackward`/`joinItemBackward` and absorbs such a
 * paragraph into the previous list item. We out-prioritise it for this one case
 * (higher extension `priority` so our keymap plugin runs first) and fall through
 * — returning `false` — for everything else, leaving all other editing intact.
 */
export const ListBoundaryKeymapExtension = Extension.create({
  name: 'listBoundaryKeymap',

  // StarterKit's list-keymap has no explicit priority (TipTap default 100).
  // A higher value sorts our keymap plugin ahead of it so we win the target case.
  priority: 1000,

  addCommands() {
    return {
      deleteEmptyParagraphAfterList:
        () =>
        ({ tr, dispatch }) => {
          if (!handleBackspace(tr)) {
            // `editor.commands.*` auto-dispatches `tr` even when the command
            // returns false; flag it so a fall-through Backspace fires no empty
            // transaction and hands off to StarterKit's list-keymap untouched.
            tr.setMeta('preventDispatch', true);
            return false;
          }
          dispatch?.(tr);
          return true;
        },
      deleteEmptyParagraphBeforeCaretList:
        () =>
        ({ tr, dispatch }) => {
          if (!handleDelete(tr)) {
            tr.setMeta('preventDispatch', true);
            return false;
          }
          dispatch?.(tr);
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () => this.editor.commands.deleteEmptyParagraphAfterList(),
      Delete: () => this.editor.commands.deleteEmptyParagraphBeforeCaretList(),
    };
  },
});
